import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { OrderStatusBadge } from '../components/billing/OrderStatusBadge';
import { OrderDetailModal } from '../components/billing/OrderDetailModal';
import { EmptyState } from '../components/common/EmptyState';
import { useOrdersQuery } from '../hooks/useSelfServiceBilling';
import { formatCents, formatBillingDate } from '../utils/billing-formatters';

const PAGE_LIMIT = 20;

// =============================================================================
// FEATURE DISABLED MESSAGE
// =============================================================================

const FeatureDisabledMessage: React.FC = () => (
  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
    <p className="text-yellow-800 font-medium">
      Zamówienia są niedostępne w tym środowisku
    </p>
  </div>
);

// =============================================================================
// MAIN PAGE (AC-7, AC-8, AC-10, AC-11)
// =============================================================================

const TenantOrders: React.FC = () => {
  const [page, setPage] = useState(0);
  const offset = page * PAGE_LIMIT;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useOrdersQuery({ limit: PAGE_LIMIT, offset });

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string>('');
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const triggerRef = useRef<HTMLTableRowElement | null>(null);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? PAGE_LIMIT;

  // Check if the API returned 404 (feature disabled)
  const isFeatureDisabled =
    isError &&
    (error as { response?: { status?: number } })?.response?.status === 404;

  const handleOpenDetail = useCallback(
    (orderId: string, orderNumber: string) => {
      triggerRef.current = rowRefs.current.get(orderId) ?? null;
      setSelectedOrderId(orderId);
      setSelectedOrderNumber(orderNumber);
    },
    [],
  );

  const handleCloseDetail = useCallback(() => {
    setSelectedOrderId(null);
    setSelectedOrderNumber('');
  }, []);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb (AC-14) */}
        <div className="mb-6">
          <Link
            to="/tenant/subscription"
            className="text-sm text-primary-700 hover:text-primary-800 hover:underline"
          >
            &larr; Wstecz do Subskrypcji
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-6">Historia zamówień</h1>

        {/* Feature disabled (ORDERING_BC_ENABLED=false) */}
        {isFeatureDisabled ? (
          <FeatureDisabledMessage />
        ) : isError ? (
          /* Error state (AC-10) */
          <div
            data-testid="orders-error"
            className="bg-red-50 border border-red-200 rounded-lg p-6"
            role="alert"
          >
            <h2 className="text-xl font-semibold text-red-800 mb-2">
              Błąd wczytywania zamówień
            </h2>
            <p className="text-red-700">
              Nie udało się załadować zamówień. Spróbuj ponownie.
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Spróbuj ponownie
            </button>
          </div>
        ) : isLoading ? (
          /* Loading state (AC-10) */
          <div data-testid="orders-loading" aria-live="polite" aria-busy="true">
            <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
              <div className="p-6 space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-12 w-full bg-gray-200 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        ) : items.length === 0 ? (
          /* Empty state (AC-10) */
          <div className="bg-white rounded-lg border border-neutral-200">
            <div data-testid="orders-empty-state">
              <EmptyState
                title="Nie masz jeszcze zamówień"
                description="Zamówienia pojawią się tutaj po złożeniu pierwszego zamówienia."
              />
            </div>
          </div>
        ) : (
          /* Order list table (AC-8) */
          <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="order-list">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Numer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Kwota brutto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Opis
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {items.map((order) => (
                    <tr
                      key={order.id}
                      ref={(el) => {
                        if (el) rowRefs.current.set(order.id, el);
                      }}
                      data-testid={`order-row-${order.id}`}
                      tabIndex={0}
                      className="hover:bg-neutral-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset"
                      onClick={() =>
                        handleOpenDetail(order.id, order.orderNumber)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenDetail(order.id, order.orderNumber);
                        }
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-neutral-900 font-mono text-sm">
                          {order.orderNumber}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                        {formatBillingDate(order.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <OrderStatusBadge status={order.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-800">
                        {formatCents(order.totalGrossCents)}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-600 max-w-xs truncate">
                        {order.description}
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-right text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() =>
                            handleOpenDetail(order.id, order.orderNumber)
                          }
                          className="text-primary-700 hover:text-primary-800 font-medium"
                        >
                          Szczegóły
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination (AC-11) */}
            {total > limit && (
              <div
                data-testid="orders-pagination"
                className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 text-sm text-gray-600"
              >
                <span>
                  Zamówienia {offset + 1}-{Math.min(offset + limit, total)} z{' '}
                  {total}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={offset === 0}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Poprzednia
                  </button>
                  <button
                    disabled={offset + limit >= total}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Następna
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail Modal (AC-9) */}
      {selectedOrderId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          orderNumber={selectedOrderNumber}
          onClose={handleCloseDetail}
          triggerRef={triggerRef}
        />
      )}
    </Layout>
  );
};

export default TenantOrders;
